"use client";

import { useState, useCallback, useRef, useEffect } from "react";
import {
  type DomainId,
  DOMAIN_LABELS,
  DOMAIN_SPACING,
  DOMAIN_COUNT,
  DOMAIN_ORDER,
  getDomainBaseAngle,
  getDomainAtFront,
  getAdjacentDomains,
} from "@/data/domains";

// =============================================================================
// META RING NAVIGATION
// =============================================================================
// Spring physics + spin animation for the meta ring.
// Domain config lives in @/data/domains -- this hook just does the math.

// Re-export for consumers that import domain types from here
export type { DomainId };
export { DOMAIN_ORDER, DOMAIN_SPACING, getAdjacentDomains };

// Spring physics constants for spin animation
const SPRING_STIFFNESS = 0.05;  // Soft for gradual orbital rotation feel
const SPRING_DAMPING = 0.82;    // Higher damping for smoother settle
const SETTLE_THRESHOLD = 0.0001;

// Spin direction for corner nav animations
export type SpinDirection = "cw" | "ccw" | null;

// Transition state for corner nav label animations
export interface SpinTransition {
  direction: SpinDirection;
  clickedCorner: "left" | "right" | null;
  fromDomain: DomainId;
  toDomain: DomainId;
  prevLeftLabel: DomainId;
  prevRightLabel: DomainId;
}

export interface UseMetaRingResult {
  activeDomain: DomainId;
  metaAngle: number;
  isSpinning: boolean;
  leftDomain: DomainId;
  rightDomain: DomainId;
  spinTransition: SpinTransition | null;
  spinTo: (domain: DomainId) => void;
  getDomainLabel: (domain: DomainId) => string;
}

export function useMetaRing(initialDomain: DomainId = "projects"): UseMetaRingResult {
  const [metaAngle, setMetaAngle] = useState(() => {
    return -getDomainBaseAngle(initialDomain);
  });

  const [isSpinning, setIsSpinning] = useState(false);
  const [spinTransition, setSpinTransition] = useState<SpinTransition | null>(null);

  const velocityRef = useRef(0);
  const targetAngleRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  const activeDomain = getDomainAtFront(metaAngle);
  const { left: leftDomain, right: rightDomain } = getAdjacentDomains(activeDomain);

  const runSpringAnimation = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }

    const animate = () => {
      const target = targetAngleRef.current;
      if (target === null) {
        setIsSpinning(false);
        setSpinTransition(null);
        return;
      }

      setMetaAngle((current) => {
        // No shortest-path normalization - ring physically rotates through all domains
        const diff = target - current;

        velocityRef.current += diff * SPRING_STIFFNESS;
        velocityRef.current *= SPRING_DAMPING;

        const newAngle = current + velocityRef.current;

        if (Math.abs(velocityRef.current) < SETTLE_THRESHOLD && Math.abs(diff) < 0.001) {
          targetAngleRef.current = null;
          velocityRef.current = 0;
          setIsSpinning(false);
          setSpinTransition(null);
          return target;
        }

        return newAngle;
      });

      if (targetAngleRef.current !== null) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };

    setIsSpinning(true);
    rafRef.current = requestAnimationFrame(animate);
  }, []);

  const spinTo = useCallback((domain: DomainId) => {
    if (domain === activeDomain) return;

    // Calculate rotation direction based on domain positions (physical ring physics)
    const currentIndex = DOMAIN_ORDER.indexOf(activeDomain);
    const targetIndex = DOMAIN_ORDER.indexOf(domain);

    // Steps in each direction (with wrap-around)
    const stepsCW = (targetIndex - currentIndex + DOMAIN_COUNT) % DOMAIN_COUNT;
    const stepsCCW = (currentIndex - targetIndex + DOMAIN_COUNT) % DOMAIN_COUNT;

    // Pick shorter direction (CW if equal) - but now we actually rotate through
    const goCW = stepsCW <= stepsCCW;
    const steps = goCW ? stepsCW : stepsCCW;

    // Determine corner (for adjacent domains) or direction (for jumps)
    const isAdjacent = domain === leftDomain || domain === rightDomain;
    const clickedCorner: "left" | "right" = domain === leftDomain ? "left" : (domain === rightDomain ? "right" : (goCW ? "right" : "left"));
    const direction: SpinDirection = goCW ? "cw" : "ccw";

    setSpinTransition({
      direction,
      clickedCorner: isAdjacent ? clickedCorner : null, // null for non-adjacent jumps
      fromDomain: activeDomain,
      toDomain: domain,
      prevLeftLabel: leftDomain,
      prevRightLabel: rightDomain,
    });

    // Calculate target angle that rotates through all intermediate domains
    // metaAngle decreases for CW rotation (domain index increases)
    // metaAngle increases for CCW rotation (domain index decreases)
    const angleChange = steps * DOMAIN_SPACING;
    const targetAngle = goCW
      ? metaAngle - angleChange
      : metaAngle + angleChange;

    targetAngleRef.current = targetAngle;
    runSpringAnimation();
  }, [activeDomain, leftDomain, rightDomain, metaAngle, runSpringAnimation]);

  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  const getDomainLabel = useCallback((domain: DomainId) => {
    return DOMAIN_LABELS[domain];
  }, []);

  return {
    activeDomain,
    metaAngle,
    isSpinning,
    leftDomain,
    rightDomain,
    spinTransition,
    spinTo,
    getDomainLabel,
  };
}
